import {
  DiscountType,
  InvoiceLine,
  PaymentTerms,
  ProInvoice,
  Product,
} from '../types/pro'
import { addDays, newLineId, toISODate } from './pro'

/**
 * Invoice arithmetic lives here so the editor, the list, the preview and the
 * statistics page can never disagree about what an invoice is worth.
 *
 * VAT is per line, and the entered unit price is either excluding or
 * including VAT depending on the invoice's own setting — switching that
 * setting must not change what the customer pays.
 */

export interface LineAmounts {
  /** Before discount, excluding VAT. */
  gross: number
  discountAmount: number
  /** After discount, excluding VAT. */
  net: number
  vat: number
  /** After discount, including VAT. */
  total: number
}

export function lineAmounts(
  line: InvoiceLine,
  pricesIncludeVat: boolean,
): LineAmounts {
  const rate = line.vatRate ?? 0
  const raw = (line.quantity || 0) * (line.unitPrice || 0)
  // Work in "as entered" money first, then split VAT off at the end.
  const entered = pricesIncludeVat ? raw / (1 + rate) : raw

  const discountAmount =
    line.discountType === 'beloeb'
      ? Math.min(line.discount || 0, entered)
      : entered * ((line.discount || 0) / 100)

  const net = Math.max(0, entered - discountAmount)
  const vat = net * rate

  return {
    gross: entered,
    discountAmount,
    net,
    vat,
    total: net + vat,
  }
}

export interface InvoiceAmounts {
  subtotal: number
  /** Discount applied across the whole invoice, excluding VAT. */
  invoiceDiscount: number
  vat: number
  total: number
  /** Sum of registered payments. */
  paid: number
  outstanding: number
}

export function invoiceAmounts(invoice: ProInvoice): InvoiceAmounts {
  const lines = invoice.lines.map((line) =>
    lineAmounts(line, invoice.pricesIncludeVat),
  )
  const lineNet = lines.reduce((sum, amounts) => sum + amounts.net, 0)
  const factor = 1 - (invoice.totalDiscount || 0) / 100

  const subtotal = lineNet
  const invoiceDiscount = lineNet * (1 - factor)
  // The whole-invoice discount reduces VAT proportionally, per line rate.
  const vat = lines.reduce((sum, amounts) => sum + amounts.vat, 0) * factor
  const total = lineNet * factor + vat
  const paid = (invoice.payments ?? []).reduce(
    (sum, payment) => sum + payment.amount,
    0,
  )

  return {
    subtotal,
    invoiceDiscount,
    vat,
    total,
    paid,
    outstanding: Math.max(0, total - paid),
  }
}

/** Convenience for lists and totals that only need the final figure. */
export function invoiceTotal(invoice: ProInvoice): number {
  return invoiceAmounts(invoice).total
}

// ---------------------------------------------------------------------------
// Payment terms
// ---------------------------------------------------------------------------

export const PAYMENT_TERMS: { id: PaymentTerms; label: string }[] = [
  { id: 'netto', label: 'Netto' },
  { id: 'netto-kontant', label: 'Netto kontant' },
  { id: 'loebende-maaned', label: 'Løbende måned' },
  { id: 'betalt', label: 'Fakturaen er allerede betalt' },
  { id: 'brugerdefineret', label: 'Brugerdefineret' },
]

export function paymentTermsLabel(
  terms: PaymentTerms,
  days: number,
): string {
  if (terms === 'netto') return `Netto ${days} dage`
  if (terms === 'loebende-maaned') return `Løbende måned + ${days} dage`
  return PAYMENT_TERMS.find((item) => item.id === terms)?.label ?? 'Netto'
}

/** Returns null for custom terms — the user owns the date in that case. */
export function dueDateFor(
  invoiceDate: string,
  terms: PaymentTerms,
  days: number,
): string | null {
  if (!invoiceDate) return null
  const base = new Date(invoiceDate)
  if (Number.isNaN(base.getTime())) return null

  switch (terms) {
    case 'netto':
      return toISODate(addDays(base, days))
    case 'netto-kontant':
    case 'betalt':
      return toISODate(base)
    case 'loebende-maaned': {
      const endOfMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0)
      return toISODate(addDays(endOfMonth, days))
    }
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Numbering, lines, status
// ---------------------------------------------------------------------------

/**
 * Danish invoice numbers run as a plain unbroken sequence — 10041, 10042.
 * Legacy records used a prefixed form, so anything below the sequence start
 * is ignored rather than dragging the counter backwards.
 */
const FIRST_INVOICE_NO = 10001

export function nextInvoiceNo(existing: string[]): string {
  const numbers = existing
    .map((value) => parseInt((value || '').replace(/\D/g, ''), 10))
    .filter((value) => !Number.isNaN(value) && value >= FIRST_INVOICE_NO)
  const next =
    numbers.length === 0 ? FIRST_INVOICE_NO : Math.max(...numbers) + 1
  return String(next)
}

export function emptyInvoiceLine(vatRate = 0.25): InvoiceLine {
  return {
    id: newLineId(),
    description: '',
    quantity: 1,
    unit: 'stk',
    unitPrice: 0,
    discount: 0,
    discountType: 'procent',
    vatRate,
  }
}

export function lineFromProduct(product: Product): InvoiceLine {
  return {
    id: newLineId(),
    description: product.name,
    quantity: 1,
    unit: product.unit,
    unitPrice: product.price,
    discount: 0,
    discountType: 'procent',
    vatRate: product.vatRate,
    productId: product.id,
  }
}

export const DISCOUNT_TYPES: { id: DiscountType; label: string }[] = [
  { id: 'procent', label: '%' },
  { id: 'beloeb', label: 'kr' },
]

/** An approved invoice is a legal document; only a draft is freely editable. */
export function isEditable(invoice: ProInvoice): boolean {
  return invoice.status === 'kladde'
}

/**
 * Fills in everything an invoice needs. Records written before the editor
 * existed have no billing block, VAT per line or payment terms, so they are
 * completed on read rather than migrated — nothing in the database breaks.
 */
export function hydrateInvoice(
  raw: Omit<Partial<ProInvoice>, 'lines'> & {
    id?: string
    lines?: Partial<InvoiceLine>[]
  },
): ProInvoice {
  return {
    id: raw.id,
    invoiceNo: raw.invoiceNo ?? '',
    customerId: raw.customerId ?? '',
    customerName: raw.customerName ?? '',
    billing: {
      ...emptyBilling(),
      name: raw.customerName ?? '',
      ...(raw.billing ?? {}),
    },
    jobId: raw.jobId,
    status: raw.status ?? 'kladde',
    invoiceDate: raw.invoiceDate ?? '',
    dueDate: raw.dueDate ?? '',
    paymentTerms: raw.paymentTerms ?? 'netto',
    paymentTermsDays: raw.paymentTermsDays ?? 8,
    pricesIncludeVat: raw.pricesIncludeVat ?? false,
    reference: raw.reference ?? '',
    orderNo: raw.orderNo ?? '',
    lines: (raw.lines ?? []).map((line) => ({
      id: line.id ?? newLineId(),
      description: line.description ?? '',
      quantity: line.quantity ?? 1,
      unit: line.unit ?? 'stk',
      unitPrice: line.unitPrice ?? 0,
      productId: line.productId,
      discount: line.discount ?? 0,
      discountType: line.discountType ?? 'procent',
      vatRate: line.vatRate ?? 0.25,
    })),
    totalDiscount: raw.totalDiscount ?? 0,
    notes: raw.notes ?? '',
    internalNote: raw.internalNote ?? '',
    payments: raw.payments ?? [],
    approvedAt: raw.approvedAt,
    sentAt: raw.sentAt,
    creditNoteOf: raw.creditNoteOf,
  }
}

export function emptyBilling() {
  return {
    name: '',
    attention: '',
    address: '',
    postalCode: '',
    city: '',
    email: '',
    phone: '',
    cvr: '',
  }
}
